import React, { useEffect, useState } from "react"
import {
  BooleanField,
  BooleanInput,
  Datagrid,
  Filter,
  List,
  NumberInput,
  SelectInput,
  TextField,
  TextInput,
  downloadCSV,
  FunctionField,
  BulkExportButton,
} from "react-admin"
import jsonExport from "jsonexport/dist"

import { Button, Box } from "@material-ui/core"
import ClearIcon from "@material-ui/icons/Clear"

import { DateRangeFilterWithField } from "../DateRangeFilter"
import TimestampField from "../TimestampField"
import DefaultActions from "../DefaultActions"
import DefaultPagination from "../DefaultPagination"
import SendBulkEmailButton from "../Claim/SendBulkEmailButton"
import SendBulkWhatsappButton from "../Claim/SendBulkWhatsappButton"
import ClaimBulkDelete from "../ClaimBulkDelete"
import BulkTransferButton from "../Claim/BulkTransferButton"
import DuplicateClaims from "../DuplicateClaims"
import { NumberRangeFilter } from "../NumberRangeFilter"
import { isArray } from "lodash"

const booleanChoices = [
  { id: "__true__", name: "Yes" },
  { id: "__false__", name: "No" },
]

function wrapInBox (component: JSX.Element) {
  return (
    // @ts-expect-error: Property 'alwaysOn' does not exist
    <Box mb={2} alwaysOn>
      {component}
    </Box>
  )
}

function ClaimFilters (
  props: {
    setFilters?: (filterMap: Record<string, any>) => void,
    filterValues?: Record<string, any>,
    displayedFilters?: Record<string, boolean>,
  },
) {
  // Remove filter when the number range is removed
  useEffect(() => {
    if (!props.setFilters) {
      return
    }

    let updateFilter = false
    if (!props.displayedFilters ||
      !Object.keys(props.displayedFilters).includes("bmwCarData.currentMileage")
    ) {
      
      if (props.filterValues?.bmwCarData?.currentMileage__gte) {
        delete props.filterValues.bmwCarData.currentMileage__gte
        updateFilter = true
      }

      if (props.filterValues?.bmwCarData?.currentMileage__lte) {
        delete props.filterValues.bmwCarData.currentMileage__lte
        updateFilter = true
      }
    }

    if (!props.displayedFilters ||
      !Object.keys(props.displayedFilters).includes("bmwCarData.soldMileage")
    ) {
      if (props.filterValues?.bmwCarData?.soldMileage__gte) {
        delete props.filterValues.bmwCarData.soldMileage__gte
        updateFilter = true
      }

      if (props.filterValues?.bmwCarData?.soldMileage__lte) {
        delete props.filterValues.bmwCarData.soldMileage__lte
        updateFilter = true
      }
    }

    if (props.filterValues && updateFilter) {
      props.setFilters(props.filterValues)
    }
  }, [props.displayedFilters])
  return (
    <Filter {...props}>
      {wrapInBox(
        <Button
          variant='outlined'
          size='small'
          onClick={() => props.setFilters && props.setFilters({
            claimActionStatus: "",
            createdTime: "",
            id: "",
            lawFirm: "",
            tag: "",
            transmittedToLawfirmAt: "",
            userEmail: "",
            userId: "",
            userIsRegistered: true,
            userRegistrationTime: "",
          })}
        >
          <ClearIcon />
          Clear Filters
        </Button>,
      )}
      <TextInput label='Filter by claim id' source='id' alwaysOn />
      <TextInput label='Filter by user id' source='userId' alwaysOn />
      <TextInput label='Filter by email' source='userEmail' alwaysOn />
      <TextInput label='Filter by law firm case id'
        source='lawFirmCaseId__number' alwaysOn/>
      <SelectInput
        label='Law Firm'
        source="lawFirm"
        alwaysOn
        choices={[
          { id: "AILTS", name: "AILTS" },
          { id: "BRC", name: "BRC" },
          { id: "KAP", name: "KAP" },
          { id: "LEO", name: "LEO" },
          { id: "Von Rüden", name: "Von Rüden" },
          { id: "Hartung", name: "Hartung" },
          { id: "DPR", name: "Redell" },
        ]}
      />
      <TextInput label='Filter by lead source' source='leadSource' alwaysOn />
      <TextInput
        label='Filter by UTM source'
        source='utmIndex.utm_source'
        alwaysOn
      />
      {wrapInBox(
        <DateRangeFilterWithField source='createdTime' label='Created At' />,
      )}
      {wrapInBox(
        <DateRangeFilterWithField 
          source='transmittedToLawfirmAt' 
          label='Transmitted to Law Firm At' 
        />,
      )}
      <SelectInput
        label="Vehicle Ownership"
        source="bmwCarData.ownership"
        choices={[
          { id: "owner", name: "Owner" },
          { id: "sold", name: "Sold" },
        ]}
      />
      <SelectInput
        source='hasUploadedPurchaseContract'
        label='KV Uploaded'
        choices={booleanChoices}
      />
      <SelectInput
        source='hasUploadedSalesContract'
        label='VV Uploaded'
        choices={booleanChoices}
      />
      <SelectInput
        source='bmwCarData.currentKmStatus'
        label='Current Mileage Valid'
        choices={booleanChoices}
      />
      <SelectInput
        source='claimActionStatus'
        choices={[
          { id: "active", name: "Active" },
          { id: "dead", name: "Dead" },
          { id: "onHold", name: "On Hold" },
          { id: "finished", name: "Finished" },
        ]}
      />
      <NumberRangeFilter
        source='bmwCarData.currentMileage'
        label='Current Mileage'
      />
      <SelectInput
        source='bmwCarData.mileage'
        choices={[
          { id: "<200k", name: "< 175.000 Km" },
          { id: ">200k", name: "> 175.000 Km" },
        ]}
        label="Vehicle Mileage"
      />
      <NumberRangeFilter
        source='bmwCarData.salesMileage'
        label='Mileage on Sold'
      />
      <SelectInput
        source='bmwCarData.mileageSold'
        choices={[
          { id: "<200k", name: "< 175.000 Km" },
          { id: ">200k", name: "> 175.000 Km" },
        ]}
        label="Vehicle Mileage on Sold"
      />
      <SelectInput
        source='poaUploaded'
        label='POA Uploaded'
        choices={booleanChoices}
      />
      <BooleanInput
        source='transmittedToLawfirmAt__exists'
        label='Transmitted to Lawfirm'
      />
    </Filter>
  )
}

function ClaimBulkActionButtons (
  props: { [x: string]: any, selectedIds: any },
) {
  return (
    <Box>
      <DuplicateClaims {...props} ocClaim={true} />
      <BulkExportButton
        {...props} 
        label="Export Selection" 
        variant="outlined" 
        size="small"
        style={{
          marginRight: "8px",
          borderColor: "white",
          color: "white",
          fontSize: "14px",
          padding: "5px 15px",
        }}
      />
      <SendBulkEmailButton {...props} />
      <SendBulkWhatsappButton {...props} />
      <BulkTransferButton {...props} />
      <ClaimBulkDelete {...props} />
    </Box>
  )
}

function exporter (claims: any) {
  const flattenedClaim = claims.map((claim: any) => {
    if (isArray(claim.utmParams)) {
      claim.utm_params = {}
      for (const param of claim.utmParams) {
        if (!param) continue
        claim.utm_params[param.source] = param.value
      }

      delete claim.utmParams
    }

    return claim
  })

  jsonExport(flattenedClaim, {
    fillGaps: true,
  }, (err, csv) => {
    downloadCSV(csv, "bmw-claims")
  })
}

export default function BmwClaimList (props: Record<string, unknown>) {
  return (
    <List
      // Permanent filter
      filter={{
        type: "bmw-claim",
      }}
      filters={<ClaimFilters />}
      // @ts-expect-error
      bulkActionButtons={<ClaimBulkActionButtons />}
      actions={<DefaultActions />}
      filterDefaultValues={{ userIsRegistered: true }}
      sort={{ field: "createdTime", order: "DESC" }}
      perPage={25}
      pagination={<DefaultPagination />}
      exporter={exporter}
      {...props}
    >
      <Datagrid rowClick='show'>
        <BooleanField
          label='Lead'
          source='lead'
          sortable={false}
        />
        <TextField label='Claim ID' source='id' sortable={false} />
        <TextField label='User ID' source='userId' sortable={false} />
        <TextField source='userEmail' sortable={false} />
        <TextField
          source='phoneNumber'
          sortable={false}
          style={{ whiteSpace: "nowrap" }}
        />
        <TextField source='claimActionStatus' sortable={false} />
        <TextField source='lawFirm' label='Law Firm' sortable={false} />
        {/* <TextField source='lawFirmCaseId'
          label='Law Firm Case ID' sortable={false} /> */}
        <TimestampField
          source='createdTime'
          locales='sv-SE'
          showTime
          sortable={false}
        />
        <TimestampField
          source='transmittedToLawfirmAt'
          locales='sv-SE'
          showTime
          sortable={false}
        />
        <TimestampField
          source='updatedTime'
          locales='sv-SE'
          showTime
          sortable={false}
        />
        <BooleanField
          source='hasUploadedPurchaseContract'
          label='KV Uploaded'
          sortable={false}
        />
        <BooleanField
          source='hasUploadedVehicleRegistrationDocument'
          label='FZS Uploaded'
          sortable={false}
        />
        <BooleanField
          source='hasUploadedSalesContract'
          label='VV Uploaded'
          sortable={false}
        />
        <BooleanField
          source='hasUploadedLatestMileage'
          label='KM Stand Uploaded'
          sortable={false}
        />
        <BooleanField
          source='poaUploaded'
          label='POA Uploaded'
          sortable={false}
        />
        <BooleanField
          source='bmwCarData.currentKmStatus'
          label='Current Mileage Valid'
          sortable={false}
        />
        <TimestampField
          source='poaUploadedAt'
          label='POA Uploaded At'
          locales='sv-SE'
          showTime
          sortable={false}
        />
        <TextField label='Campaign ID'
          source='campaignid' />
        <TextField label='Ad Group Id'
          source='adgroupid' />
        <FunctionField
          source="utmParams"
          label='Utm Source'
          render={record => {
            if (!record?.utmParams) {
              return ""
            }

            const utmSource = record?.utmParams.find(
              (utm: { source: string; value: any; } | null) =>
                utm?.source === "utm_source",
            )

            return utmSource?.value
          }}
        />
        <TextField source='leadSource' label='Lead Source' sortable={false} />

      </Datagrid>
    </List>
  )
}
